/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * Simple ol' Me calculator colour-preview lightbox.
 * Scoped to the Fabric quote v2 preview image only.
 * The preview renderer remains authoritative; this code only makes the
 * rendered preview image directly clickable/tappable for enlargement.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[id^="custom-bedding-quote-v2-"]';
  const IMAGE_SELECTOR = '.som-colour-preview-r2__image';
  const DATA_SECTION_ID = 'som-colour-preview-data';
  const LIGHTBOX_ID = 'som-colour-preview-direct-lightbox';
  const STYLE_ID = 'som-colour-preview-direct-lightbox-style';
  let zoomMapPromise = null;
  let lastTrigger = null;
  let activeOpenToken = 0;

  function installStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      ${ROOT_SELECTOR} ${IMAGE_SELECTOR}{cursor:zoom-in!important;touch-action:manipulation;transition:filter .15s ease,opacity .15s ease}
      ${ROOT_SELECTOR} ${IMAGE_SELECTOR}:hover{filter:brightness(.975)}
      ${ROOT_SELECTOR} ${IMAGE_SELECTOR}:focus-visible{outline:3px solid #566b59;outline-offset:3px;border-radius:4px}
      body.som-colour-preview-direct-modal-open{overflow:hidden!important}
      .som-colour-preview-direct-lightbox[hidden]{display:none!important}
      .som-colour-preview-direct-lightbox{position:fixed;inset:0;z-index:2147483600;display:flex;align-items:center;justify-content:center;padding:62px 20px 24px;background:rgba(0,0,0,.88);box-sizing:border-box;cursor:zoom-out;overscroll-behavior:contain}
      .som-colour-preview-direct-lightbox__image{display:block;max-width:min(95vw,2000px);max-height:calc(100vh - 86px);width:auto;height:auto;object-fit:contain;border-radius:8px;background:#fff;box-shadow:0 18px 55px rgba(0,0,0,.42);cursor:default}
      .som-colour-preview-direct-lightbox__close{position:fixed;top:max(12px,env(safe-area-inset-top));right:max(12px,env(safe-area-inset-right));z-index:1;display:grid;place-items:center;width:44px;height:44px;padding:0;border:1px solid rgba(255,255,255,.6);border-radius:999px;background:rgba(18,18,18,.88);color:#fff;font:400 30px/1 Arial,sans-serif;cursor:pointer}
      .som-colour-preview-direct-lightbox__close:focus-visible{outline:3px solid #fff;outline-offset:3px}
      @media(max-width:699px){
        .som-colour-preview-direct-lightbox{padding:62px 8px 14px}
        .som-colour-preview-direct-lightbox__image{max-width:calc(100vw - 16px);max-height:calc(100dvh - 78px);border-radius:6px}
      }
    `;
    document.head.appendChild(style);
  }

  function ensureLightbox() {
    let overlay = document.getElementById(LIGHTBOX_ID);
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.id = LIGHTBOX_ID;
    overlay.className = 'som-colour-preview-direct-lightbox';
    overlay.hidden = true;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', '顏色效果大圖預覽 / Enlarged colour preview');
    overlay.innerHTML = `
      <button type="button" class="som-colour-preview-direct-lightbox__close" aria-label="關閉大圖 / Close enlarged preview">×</button>
      <img class="som-colour-preview-direct-lightbox__image" alt="">
    `;
    document.body.appendChild(overlay);

    const closeButton = overlay.querySelector('.som-colour-preview-direct-lightbox__close');
    const modalImage = overlay.querySelector('.som-colour-preview-direct-lightbox__image');

    const close = () => {
      if (overlay.hidden) return;
      activeOpenToken += 1;
      overlay.hidden = true;
      document.body.classList.remove('som-colour-preview-direct-modal-open');
      modalImage.removeAttribute('src');
      modalImage.alt = '';
      const trigger = lastTrigger;
      lastTrigger = null;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };

    overlay._somDirectClose = close;
    closeButton.addEventListener('click', close);
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close();
    });
    document.addEventListener('keydown', (event) => {
      if (overlay.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        closeButton.focus({ preventScroll: true });
      }
    });

    return overlay;
  }

  function openLightbox(image) {
    if (!image) return;
    const fallbackSrc = image.currentSrc || image.src || '';
    if (!fallbackSrc) return;

    const overlay = ensureLightbox();
    const modalImage = overlay.querySelector('.som-colour-preview-direct-lightbox__image');
    const closeButton = overlay.querySelector('.som-colour-preview-direct-lightbox__close');
    const token = ++activeOpenToken;
    lastTrigger = image;
    modalImage.src = fallbackSrc;
    modalImage.alt = image.alt || '';
    overlay.hidden = false;
    document.body.classList.add('som-colour-preview-direct-modal-open');
    requestAnimationFrame(() => closeButton.focus({ preventScroll: true }));

    const item = image.closest('.cbq-v2__item');
    const code = String(item?.querySelector('.item-colour')?.value || '').trim().toUpperCase();
    if (!code) return;

    loadZoomMap().then((map) => {
      if (overlay.hidden || token !== activeOpenToken) return;
      const zoomSrc = map.get(code);
      if (zoomSrc) modalImage.src = zoomSrc;
    }).catch(() => {});
  }

  async function loadZoomMap() {
    if (zoomMapPromise) return zoomMapPromise;
    zoomMapPromise = (async () => {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('section_id', DATA_SECTION_ID);
      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Colour preview data HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('[data-som-colour-preview-json]');
      if (!node) throw new Error('Colour preview data not found');
      const rows = JSON.parse(node.textContent || '[]');
      const map = new Map();
      rows.forEach((row) => {
        const code = String(row?.code || '').trim().toUpperCase();
        const src = row?.previewZoomUrl || row?.previewUrl || '';
        if (code && src && row?.active !== false) map.set(code, String(src));
      });
      return map;
    })().catch((error) => {
      zoomMapPromise = null;
      console.warn('[SOM Colour Preview] High-resolution image lookup failed; using displayed image.', error);
      throw error;
    });
    return zoomMapPromise;
  }

  function enhanceImage(image) {
    if (!image || image.dataset.somDirectZoomReady === '1') return;
    image.dataset.somDirectZoomReady = '1';
    image.tabIndex = 0;
    image.setAttribute('role', 'button');
    image.setAttribute('aria-label', `${image.alt || 'Colour preview'} — 點擊放大 / Click to enlarge`);
    image.title = '點擊放大 / Click to enlarge';
  }

  function enhanceRoot(root) {
    if (!root) return;
    root.querySelectorAll(IMAGE_SELECTOR).forEach(enhanceImage);
    if (root.dataset.somDirectZoomObserver === '1') return;
    root.dataset.somDirectZoomObserver = '1';
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        root.querySelectorAll(IMAGE_SELECTOR).forEach(enhanceImage);
      });
    }).observe(root, { childList: true, subtree: true });
  }

  function boot(scope = document) {
    installStyles();
    ensureLightbox();
    scope.querySelectorAll(ROOT_SELECTOR).forEach(enhanceRoot);
  }

  document.addEventListener('click', (event) => {
    const image = event.target.closest?.(IMAGE_SELECTOR);
    if (!image || !image.closest(ROOT_SELECTOR)) return;
    event.preventDefault();
    event.stopPropagation();
    openLightbox(image);
  }, true);

  document.addEventListener('keydown', (event) => {
    const image = event.target.closest?.(IMAGE_SELECTOR);
    if (!image || !image.closest(ROOT_SELECTOR)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openLightbox(image);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
